import React, { useEffect, useMemo, useState } from "react";
import PushDrawer from "@/components/ui/push-drawer";
import Text from "@/components/ui/text";
import { useTranslation } from "react-i18next";
import Spinner from "@/components/ui/spinner";
import { Add, BackArrowLeft, Close } from "@dreamhub/designsystem/icons";
import { Button } from "@/components/ui/button";
import Task from "@/components/controls/Task/Task";
import { CheckboxGroup } from "@/components/ui/checkbox-group";
import { SortingState } from "@tanstack/react-table";
import TaskModal from "@/components/layouts/TasksDrawer/TaskModal";
import { TaskModalState } from "@/entities/tasks/types";
import { createModalStateSetter } from "@/entities/modals/helpers";
import FilterButton from "@/components/controls/Filters/FilterButton";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { useFilteredTasks } from "@/entities/tasks/hooks";
import { EntityData, Filters } from "@/services/api/types";
import SearchButtonInput from "@/components/ui/search-button-input";
import EmptyTasksState from "@/components/layouts/TasksDrawer/EmptyTasksState";
import { IconButton } from "@/components/ui/icon-button";
import { isEmpty, map } from "lodash";

const TRANSLATION_KEY = "tasks.drawer";
const sortByOptions = ["dueDate", "isHighPriority", "assigneeId", "createdAt"];
// Priority is a boolean, so flagged tasks only come first when sorted descending
const DESC_BY_DEFAULT_SORT_IDS = new Set(["isHighPriority"]);
const DEFAULT_SORTING = [
  {
    id: "dueDate",
    desc: false
  }
];
const DEFAULT_COMPLETED_SORT_ID = "completedAt";
const DEFAULT_COMPLETED_SORT_DESC = true;

interface Props {
  isOpen: boolean;
  toggleDrawer: () => void;
  entityData: EntityData;
  entityType: EntityType;
}

const TasksDrawer: React.FC<Props> = ({
  isOpen,
  toggleDrawer,
  entityData,
  entityType
}) => {
  const { t } = useTranslation();
  const DEFAULT_FILTERS = {
    entityIds: { contains: entityData.id || "" },
    isCompleted: false
  };

  const [sorting, onSortingChange] = useState<SortingState>(DEFAULT_SORTING);
  const [taskModalState, setTaskModalState] = useState<TaskModalState>({
    isOpen: false,
    isEditMode: false,
    editTaskId: null
  });
  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);
  const [searchTerm, setSearchTerm] = useState("");

  const isCompletedTasksView = filters?.isCompleted === true;
  const sortId = isCompletedTasksView
    ? DEFAULT_COMPLETED_SORT_ID
    : sorting[0].id;
  const sortAsc = isCompletedTasksView
    ? !DEFAULT_COMPLETED_SORT_DESC
    : !sorting[0].desc;

  const { entities: tasks, isLoading: isLoadingTasks } = useFilteredTasks(
    filters,
    searchTerm ? { term: searchTerm } : undefined,
    undefined,
    undefined,
    sortId,
    sortAsc
  );

  const { entities: completedTasks, isLoading: isLoadingCompletedTasks } =
    useFilteredTasks({ ...DEFAULT_FILTERS, isCompleted: true });

  const setIsTaskModalOpen = createModalStateSetter(setTaskModalState);

  useEffect(() => {
    if (!isOpen) {
      setFilters(DEFAULT_FILTERS);
      setSearchTerm("");
      onSortingChange(DEFAULT_SORTING);
    }
  }, [isOpen]);

  const sortParams = useMemo(
    () =>
      sorting.length > 0
        ? {
            field: sorting[0].id,
            asc: !sorting[0].desc
          }
        : undefined,
    [sorting]
  );

  const sortByOptionMap = useMemo(() => {
    return Object.fromEntries(
      sortByOptions.map((key) => [
        key,
        t(`${TRANSLATION_KEY}.sortByOptions.${key}`)
      ])
    );
  }, [sortByOptions, t]);

  const toggleCompletedTasks = () =>
    setFilters({ ...filters, isCompleted: !filters?.isCompleted });

  return (
    <>
      <PushDrawer
        isOpen={isOpen}
        className="h-full overflow-hidden"
        data-test="tasks-drawer"
      >
        <div
          className="px-4 py-3 flex space-x-3 justify-between w-full items-center border-b-[0.5px] border-border-border1"
          data-test="tasks-drawer-header"
        >
          <div className="flex gap-3 items-center">
            {isCompletedTasksView && (
              <IconButton
                size="md"
                variant="iconNoBg"
                onClick={toggleCompletedTasks}
                data-test="tasks-drawer-back-button"
              >
                <BackArrowLeft />
              </IconButton>
            )}
            <Text variant="lgSubtitleH1" data-test="tasks-drawer-title">
              {t(
                `${TRANSLATION_KEY}.${isCompletedTasksView ? "completedTitle" : "title"}`
              )}
            </Text>
          </div>
          <div className="flex items-center gap-1" data-test="tasks-drawer-actions">
            <SearchButtonInput
              placeholder={t("search")}
              inputValue={searchTerm}
              setInputValue={setSearchTerm}
              size="small"
              iconButtonSize="lg"
              data-test="tasks-drawer-search"
            />
            <IconButton
              size="lg"
              variant="iconNoBg"
              onClick={toggleDrawer}
              data-test="tasks-drawer-close-button"
            >
              <Close />
            </IconButton>
          </div>
        </div>
        <div
          className="p-4 flex flex-col gap-4 flex-1 min-h-0 overflow-y-auto overflow-x-hidden"
          data-test="tasks-drawer-content"
        >
          {!isCompletedTasksView && (
            <div
              className="flex items-center w-full justify-between"
              data-test="tasks-drawer-toolbar"
            >
              <Button
                className="flex gap-1 h-fit w-fit font-semibold"
                variant="tertiary"
                data-test="tasks-drawer-add-button"
                onClick={() =>
                  setTaskModalState({
                    editTaskId: null,
                    isEditMode: false,
                    isOpen: true
                  })
                }
              >
                <Add />
                <span>{t(`${TRANSLATION_KEY}.addNew`)}</span>
              </Button>
              {sortParams && !isEmpty(tasks) && (
                <FilterButton
                  label={t("datatoolbar.filters.sortBy")}
                  placeholder={t("datatoolbar.filters.sortBy")}
                  shouldRenderArrow
                  onArrowClick={() =>
                    onSortingChange([
                      { id: sortParams.field, desc: sortParams.asc }
                    ])
                  }
                  onDropdownValueSelected={(value: string) =>
                    onSortingChange([
                      { id: value, desc: DESC_BY_DEFAULT_SORT_IDS.has(value) }
                    ])
                  }
                  defaultDropdownValue={sortParams.field}
                  options={sortByOptionMap}
                  isAsc={sortParams.asc}
                />
              )}
            </div>
          )}
          {isLoadingTasks || isLoadingCompletedTasks ? (
            <div className="w-full h-full flex justify-center items-center">
              <Spinner size="lg" />
            </div>
          ) : isEmpty(tasks) ? (
            searchTerm.trim() === "" ? (
              <EmptyTasksState data-test="tasks-drawer-empty-state" />
            ) : (
              <div className="w-full h-full flex justify-center items-center">
                <Text variant="bodySecondary">
                  {t("tables.emptyState.title")}
                </Text>
              </div>
            )
          ) : (
            <div
              className="flex flex-col gap-5 max-h-full overflow-y-auto overflow-x-hidden"
              data-test="tasks-drawer-list"
            >
              <CheckboxGroup fullWidth color="var(--cta)">
                {map(tasks, (task) => (
                  <Task
                    key={task.id}
                    task={task}
                    setTaskModalState={setTaskModalState}
                    isCompletedTasksView={isCompletedTasksView}
                    data-test={`tasks-drawer-task-${task.id}`}
                  />
                ))}
              </CheckboxGroup>
            </div>
          )}
        </div>
        <div className="shrink-0 bg-surface-elevation0 w-full p-4">
          {!isCompletedTasksView && !isEmpty(completedTasks) && (
            <Button
              className="h-fit w-fit"
              variant="tertiary"
              onClick={toggleCompletedTasks}
              data-test="tasks-drawer-view-completed-button"
            >
              <span>
                {t(`${TRANSLATION_KEY}.completedTasks`, {
                  num: completedTasks?.length
                })}
              </span>
            </Button>
          )}
        </div>
      </PushDrawer>
      {taskModalState.isOpen && (
        <TaskModal
          isOpen={taskModalState.isOpen}
          setIsOpen={setIsTaskModalOpen}
          entityData={entityData}
          entityType={entityType}
          isEditMode={taskModalState.isEditMode}
          editTaskId={taskModalState.editTaskId}
          editTaskData={taskModalState.editTaskData}
          data-test="tasks-drawer-modal"
        />
      )}
    </>
  );
};

export default TasksDrawer;
